/**
 * Ancre (#…) de chaque section d'une page — UNE seule règle, partagée par la
 * vitrine (Home.jsx, TemplateSections.jsx) et le Studio de thème
 * (SettingsPanel.jsx), pour que l'ancre affichée dans le Studio soit
 * exactement celle qui fonctionne sur le site.
 *
 * Par ordre de priorité :
 *  1. `props.anchor_id` saisi par l'admin (sections qui exposent ce champ) ;
 *  2. l'ancre codée dans le composant de la section lui-même (`<section
 *     id="tarifs">`) — voir COMPONENT_ANCHORS, à tenir à jour avec les
 *     composants ;
 *  3. sinon un identifiant dérivé du type de section (« faq-section »,
 *     « faq-section-2 » pour la seconde du même type), posé par la vitrine
 *     sur le conteneur de la section. Ainsi TOUTE section est atteignable
 *     par un lien #… sans configuration.
 *
 * `own` dit si l'ancre est déjà portée par le composant (cas 1 et 2) : le
 * conteneur ne doit alors pas la répéter, un id en double dans la page est
 * invalide.
 */
export const COMPONENT_ANCHORS = {
  default: { services: "services", process: "processus", pricing: "tarifs", contact: "contact" },
  agence_v1: {
    services: "services", pricing_plans: "tarifs", work_carousel: "realisations",
    testimonials: "avis", tech_stack: "techno", contact: "contact",
  },
  couture_v1: { opening_hours: "horaires", contact_form: "contact", access_map: "acces" },
};

import { parseSharedType } from "@/themes/shared";

// Ancre codée dans le composant d'une section — y compris pour une section
// PARTAGÉE (importée d'un autre moteur sous un type préfixé, voir
// themes/shared.js) : le composant est celui du moteur d'origine, son ancre
// aussi (« agence_pricing_plans » sur une boutique "default" → « tarifs »).
export function componentAnchor(engine, type) {
  const own = (COMPONENT_ANCHORS[engine] || {})[type];
  if (own) return own;
  const shared = parseSharedType(type);
  return shared ? (COMPONENT_ANCHORS[shared.origin] || {})[shared.base] || "" : "";
}

export function slugifyAnchor(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** { [sectionId]: { anchor, own } } pour toutes les sections d'une page. */
export function resolveAnchors(sections, engine = "default") {
  const list = [...(sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const out = {};
  const used = new Set();

  // Les ancres fixées (par l'admin ou par le composant) d'abord : les ancres
  // dérivées ne doivent jamais entrer en collision avec elles.
  list.forEach((s) => {
    const id = s.id || s.type || s.key;
    const type = s.type || s.key;
    const custom = slugifyAnchor(s.props?.anchor_id);
    const fixed = custom || componentAnchor(engine, type);
    if (fixed) {
      out[id] = { anchor: fixed, own: true };
      used.add(fixed);
    }
  });
  list.forEach((s) => {
    const id = s.id || s.type || s.key;
    if (out[id]) return;
    const base = slugifyAnchor(s.type || s.key) || "section";
    let anchor = base;
    for (let n = 2; used.has(anchor); n += 1) anchor = `${base}-${n}`;
    used.add(anchor);
    out[id] = { anchor, own: false };
  });
  return out;
}

export function sectionAnchor(sections, engine, sectionId) {
  return resolveAnchors(sections, engine)[sectionId]?.anchor || "";
}
